/**
 * Naming ONE message from outside the thread — pure.
 *
 * A message is addressed by its `Message.id`, but every deep link minted before the
 * schema consolidation carries the legacy id instead (`?message-id=aB3…`, `?comment-id=…`,
 * `?thread-id=…`, and the short links mailed to homeowners), which the message still
 * holds as `oldId`. Both are 24-char base36 nanoids, so nothing about the value says which
 * one arrived — the match therefore checks both fields, once, here, and every consumer
 * downstream holds a `ChannelMessageTarget` rather than a bare string.
 *
 * Resolution is best-effort by construction: a legacy id can only be turned into a message
 * by finding it, so a link into a message that has not been paged in yet stays unresolved
 * until it is (see `_hooks/use-resolved-message-target.ts`, which pages towards it).
 */

/** The id as it arrived — a `Message.id` or a legacy `oldId`; the caller cannot tell. */
export type ChannelMessageTarget = {
  id: string;
  /**
   * Bump to ask for the SAME message again.
   *
   * A jump is deliberately idempotent: the view scrolls to a target once and never
   * again, so an optimistic insert or a realtime arrival cannot yank the viewport off
   * what the reader is looking at (`useScrollToMessage`). That is also why a second
   * click on one search result would otherwise do nothing at all. A changed `nonce`
   * makes the ask a new request without making it a new message.
   *
   * Absent for a deep link — a URL names a message once.
   */
  nonce?: number;
};

/** The minimum a message must carry to be addressable by either id. */
type IdentifiableMessage = {
  id: string;
  oldId?: string | null;
};

/** Reads a URL query param into a target; blank is no target. */
export function parseMessageTargetParam(
  raw: string | null | undefined,
): ChannelMessageTarget | null {
  if (raw == null) return null;

  const value = String(raw).trim();
  if (value === "") return null;

  return { id: value };
}

/** Stable identity for effect dependencies — two equal targets produce one key. */
export function messageTargetKey(
  target: ChannelMessageTarget | null | undefined,
): string | null {
  if (!target?.id) return null;
  // The nonce belongs in the key: a repeat ask must also reset the backwards-paging
  // budget in `useResolvedMessageTarget`, or a second jump to a message that has since
  // scrolled out of the loaded window would have no attempts left to walk back to it.
  return target.nonce == null
    ? `id:${target.id}`
    : `id:${target.id}#${target.nonce}`;
}

/**
 * Finds the targeted message inside a loaded window, by `id` or by `oldId`.
 *
 * @param target - What the caller is looking for.
 * @param messages - The messages currently loaded (any order).
 * @returns The match, or `undefined` when the window does not contain it.
 */
export function findTargetMessage<TMessage extends IdentifiableMessage>(
  target: ChannelMessageTarget | null | undefined,
  messages: TMessage[],
): TMessage | undefined {
  const wanted = target?.id;
  if (!wanted) return undefined;

  return messages.find(
    (message) => message.id === wanted || message.oldId === wanted,
  );
}
